// src/hooks/useCustomer.js
import { useState, useEffect, useCallback } from "react";
import customerService from "@/services/customerService";
import { toast } from "react-hot-toast";

/**
 * Helper: convert snake_case -> camelCase (no-op for camelCase keys)
 */
const snakeToCamel = (s) =>
  s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const objectToCamel = (obj = {}) => {
  if (!obj || typeof obj !== "object") return obj;
  const out = {};
  Object.keys(obj).forEach((k) => {
    const camel = snakeToCamel(k);
    out[camel] = obj[k];
  });
  return out;
};

/**
 * mapToApiFormat: frontend -> API payload shape (camelCase fields, safe defaults)
 */
const mapToApiFormat = (data = {}) => ({
  name: (data.name ?? "").trim(),
  phone: (data.phone ?? "").trim(),
  email: (data.email ?? "").trim() || "",
  address: (data.address ?? "").trim() || "",
  notes: data.notes ?? "",

  dateOfBirth: data.dateOfBirth ?? "",
  anniversaryDate: data.anniversaryDate ?? "",
  customerType: data.customerType ?? "regular",
  loyaltyPoints:
    data.loyaltyPoints !== undefined && data.loyaltyPoints !== null
      ? Number(data.loyaltyPoints)
      : 0,
  preferredPaymentMode: data.preferredPaymentMode ?? "",
  dietaryPreferences: data.dietaryPreferences ?? "",
  isActive:
    data.isActive !== undefined && data.isActive !== null
      ? !!data.isActive
      : true,

  // include id if present (update path expects id separately; create should remove)
  id: data.id ?? undefined,
});

/**
 * mapFromApiFormat: API -> frontend shape (expects camelCase keys already;
 * if server returns snake_case, we convert first)
 */
const mapFromApiFormat = (data = {}) => ({
  id: data.id ?? 0,
  name: data.name ?? "",
  phone: data.phone ?? "",
  email: data.email ?? "",
  address: data.address ?? "",
  notes: data.notes ?? "",

  dateOfBirth: data.dateOfBirth ?? "",
  anniversaryDate: data.anniversaryDate ?? "",
  customerType: data.customerType ?? "regular",
  loyaltyPoints:
    data.loyaltyPoints !== undefined && data.loyaltyPoints !== null
      ? Number(data.loyaltyPoints)
      : 0,
  preferredPaymentMode: data.preferredPaymentMode ?? "",
  dietaryPreferences: data.dietaryPreferences ?? "",
  isActive:
    data.isActive !== undefined && data.isActive !== null
      ? !!data.isActive
      : true,

  // branch info
  branchId: data.branchId ?? "",
  branchName: data.branchName ?? "",

  // meta if provided
  createdAt: data.createdAt,
  updatedAt: data.updatedAt,
  createdBy: data.createdBy,
  updatedBy: data.updatedBy,
});

/**
 * If server returns partial object (nulls), prefer server's truthy values,
 * fallback to what we sent in payload so UI shows expected values.
 */
const safeMergeFromPayload = (serverObj = {}, sentPayload = {}) => {
  const merged = { ...serverObj };
  Object.keys(sentPayload).forEach((k) => {
    const serverVal = serverObj[k];
    const sentVal = sentPayload[k];
    const serverHasValue =
      serverVal !== undefined && serverVal !== null && serverVal !== "";
    if (!serverHasValue && (sentVal !== undefined && sentVal !== null)) {
      merged[k] = sentVal;
    }
  });
  return merged;
};

/** Extract useful data from API response (handles common shapes) */
const extractDataArray = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.data?.data)) return response.data.data;
  // maybe API returns object for single item
  return [];
};

const extractSingle = (response) => {
  if (!response) return null;
  if (response.data && typeof response.data === "object" && !Array.isArray(response.data))
    return response.data;
  if (response.data?.data && typeof response.data.data === "object")
    return response.data.data;
  if (typeof response === "object" && !Array.isArray(response)) return response;
  return null;
};

export const useCustomer = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchCustomers = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const res = await customerService.getAll(filters);
      // try various response shapes and normalize keys to camelCase if needed
      const data =
        Array.isArray(res) ? res :
        Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.data?.data) ? res.data.data :
        [];

      // Normalize possible snake_case -> camelCase for each returned object
      const normalized = data.map((item) => {
        const candidate = objectToCamelIfNeeded(item);
        return mapFromApiFormat(candidate);
      });

      setCustomers(normalized);
    } catch (error) {
      console.error("Fetch Failed:", error);
      toast.error("Failed to fetch customers");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Helper: if object keys contain underscores, convert to camel first
  const objectToCamelIfNeeded = (obj = {}) => {
    if (!obj || typeof obj !== "object") return obj;
    const hasUnderscore = Object.keys(obj).some((k) => k.includes("_"));
    return hasUnderscore ? objectToCamel(obj) : obj;
  };

  const createCustomer = async (data) => {
    try {
      const payload = mapToApiFormat(data);

      // For create, API might not want id
      if (!payload.id) delete payload.id;

      // ensure isActive true
      if (payload.isActive === undefined || payload.isActive === null) {
        payload.isActive = true;
      }

      console.debug("[Customer] create - payload:", payload);

      const res = await customerService.create(payload);

      console.debug("[Customer] create - raw response:", res);

      // Extract server returned item (if present)
      const serverItemRaw = extractSingle(res) || {};

      // Normalize possible snake_case in server response
      const serverItem = objectToCamelIfNeeded(serverItemRaw);

      // Merge server values with sent payload so view has expected fields
      const final = safeMergeFromPayload(serverItem, payload);

      const mapped = mapFromApiFormat(final);

      // Prepend to local list to immediately show in UI (optimistic)
      setCustomers((prev) => [mapped, ...prev]);

      toast.success(`Customer "${mapped.name}" created & linked`);
      return mapped;
    } catch (error) {
      console.error("Create failed:", error);
      toast.error("Failed to create");
      throw error;
    }
  };

  const updateCustomer = async (id, data) => {
    try {
      const payload = mapToApiFormat(data);

      // In many APIs id is passed in URL, but include if backend expects it
      if (payload.id === undefined || payload.id === null) payload.id = id;

      console.debug(`[Customer] update(${id}) - payload:`, payload);

      const res = await customerService.update(id, payload);

      console.debug(`[Customer] update(${id}) - raw response:`, res);

      const serverItemRaw = extractSingle(res) || {};

      const serverItem = objectToCamelIfNeeded(serverItemRaw);

      const final = safeMergeFromPayload(serverItem, payload);

      const mapped = mapFromApiFormat(final);

      // update local cache
      setCustomers((prev) => prev.map((c) => (c.id === mapped.id ? mapped : c)));

      toast.success("Customer updated");
      return mapped;
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update");
      throw error;
    }
  };

  const deleteCustomer = async (id) => {
    try {
      await customerService.delete(id);
      toast.success("Customer deleted");
      setCustomers((prev) => prev.filter((c) => c.id !== id));
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete");
      throw error;
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return {
    customers,
    loading,
    fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  };
};

export default useCustomer;
